/**
 * App Component
 * Main application component with routing and providers
 */

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './contexts/ToastContext';
import LiveHelpButton from './components/layout/LiveHelpButton';
import ProfileGate from './components/auth/ProfileGate';

// Lazy load pages for code splitting
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const UploadPage = React.lazy(() => import('./pages/UploadPage'));
const CompliancePage = React.lazy(() => import('./pages/CompliancePage'));
const ResultsPage = React.lazy(() => import('./pages/ResultsPage'));
const KioskStartPage = React.lazy(() => import('./pages/KioskStartPage'));
const PublicReportPage = React.lazy(() => import('./pages/PublicReportPage'));
// Password-gated prospect demo (docs/PROSPECT_DEMO_SANDBOX_PLAN.md). Code-split:
// adds nothing to the portal's initial bundle.
const DemoApp = React.lazy(() => import('./demo/DemoApp'));

/**
 * Loading Spinner Component
 */
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthContext();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Every protected route, including sessions restored on page load, asks
  // for missing profile fields first (plan §3.3).
  return <ProfileGate>{children}</ProfileGate>;
};

/**
 * Public Route Component
 * Redirects to dashboard if user is already authenticated
 */
interface PublicRouteProps {
  children: React.ReactNode;
  redirectIfAuthenticated?: boolean;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ 
  children, 
  redirectIfAuthenticated = false 
}) => {
  const { isAuthenticated, isLoading } = useAuthContext();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated && redirectIfAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

/**
 * Clinics Redirect Component
 * Redirects /clinics to /compliance?view=clinics while preserving query params
 */
const ClinicsRedirect: React.FC = () => {
  const location = useLocation();
  const currentSearch = location.search;
  const newSearch = currentSearch
    ? `${currentSearch}&view=clinics`
    : '?view=clinics';
  return <Navigate to={`/compliance${newSearch}`} replace />;
};

/**
 * App Routes Component
 * Defines all application routes
 */
const AppRoutes: React.FC = () => {
  const { isKioskMode } = useAuthContext();

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/" 
          element={
            isKioskMode ? (
              <Navigate to="/kiosk-start" replace />
            ) : (
              <PublicRoute redirectIfAuthenticated>
                <LandingPage />
              </PublicRoute>
            )
          } 
        />
        
        <Route 
          path="/login" 
          element={
            <PublicRoute redirectIfAuthenticated>
              <LoginPage />
            </PublicRoute>
          } 
        />
        
        {/* Kiosk Routes */}
        <Route 
          path="/kiosk-start" 
          element={<KioskStartPage />} 
        />
        
        <Route 
          path="/kiosk-continue" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } 
        />

        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/upload" 
          element={
            <ProtectedRoute>
              <UploadPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/compliance" 
          element={
            <ProtectedRoute>
              <CompliancePage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/results" 
          element={
            <ProtectedRoute>
              <ResultsPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/students/:studentId" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/clinics"
          element={
            <ProtectedRoute>
              <ClinicsRedirect />
            </ProtectedRoute>
          }
        />

        {/* Prospect demo. Independent of guardian auth (no ProtectedRoute /
            PublicRoute redirect): access is controlled by the server-verified
            demo password gate inside DemoApp. */}
        <Route path="/demo/*" element={<DemoApp />} />

        {/* Public Report Route (accessible without auth) */}
        <Route
          path="/public-report/:studentId"
          element={<PublicReportPage />}
        />

        {/* Help Route (accessible without auth) */}
        <Route 
          path="/help" 
          element={<LandingPage />} 
        />

        {/* 404 - Redirect to home */}
        <Route 
          path="*" 
          element={<Navigate to="/" replace />} 
        />
      </Routes>
    </Suspense>
  );
};

/**
 * One global Live Help button; on /demo/* it switches to the demo context
 * (hidden until the demo password gate is passed, demo token endpoint).
 */
const ContextualLiveHelp: React.FC = () => {
  const location = useLocation();
  const isDemo = location.pathname === '/demo' || location.pathname.startsWith('/demo/');
  // `key` remounts the button so a Live session never carries across contexts.
  return <LiveHelpButton key={isDemo ? 'demo' : 'portal'} context={isDemo ? 'demo' : 'portal'} />;
};

/**
 * App Component
 * Wraps application with providers
 */
const App: React.FC = () => {
  // Voice "Live Help" is opt-in via VITE_ENABLE_LIVE_HELP. Credentials are
  // never shipped to the browser: the client fetches a short-lived ephemeral
  // token from the backend (/api/live-help/token) at connect time.
  const liveHelpEnabled = import.meta.env.VITE_ENABLE_LIVE_HELP === 'true';

  return (
    <Router>
      <ThemeProvider>
        <LanguageProvider>
          <ToastProvider>
            <AuthProvider>
              <div className="app">
                <AppRoutes />
                {liveHelpEnabled && <ContextualLiveHelp />}
              </div>
            </AuthProvider>
          </ToastProvider>
        </LanguageProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;